import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Button} from "@material-ui/core";
import useApplyCoupon from "../../Utils/UseApplyCoupon";
import BasicTable from "../Tables/BasicTable";
import LoadingSpinner from "../LoadingSpinner";
import useServiceUrls from "../../Utils/UseServiceUrls";
import useGetCart from "../../Utils/UseGetCart";
import useAlert from "../../Utils/UseAlert";
import {areAnyLoading, finishAlertSnippetClose, getQueryString, getReturnUrl, hasCartIdentifier} from "../../Helpers/Globals";
import Header from "../Header";
import CloseButton from '../PageControls/CloseButton'
import useAcknowledgeAlert from "../../Utils/UseAcknowledgeAlert";
import AlertMessage from "./AlertMessage";

export default function CouponAlertSnippet(props) {
    const [alertUid, setAlertUid] = useState('')
    const [domain, setDomain] = useState('')
    const [sessionKey, setSessionKey] = useState('')
    const [cartUid, setCartUid] = useState('')
    const [cartExternalId, setCartExternalId] = useState('');
    const [cartSourceExternalId, setCartSourceExternalId] = useState('');
    const [returnUrl, setReturnUrl] = useState('')
    const [coupons, setCoupons] = useState([])
    const [applyCouponCode, setApplyCouponCode] = useState('')
    const { serviceUrlsStatus, serviceUrls, serviceUrlsError } = useServiceUrls(domain);
    const { status: cartStatus, cartResponse, error: cartError, refetch } = useGetCart(serviceUrls, sessionKey, cartUid, cartExternalId, cartSourceExternalId);
    const { status: alertStatus, alert, error: alertError } = useAlert(serviceUrls, sessionKey, alertUid);

    const acknowledge = useAcknowledgeAlert();
    const apply = useApplyCoupon()
    const acknowledgedRef = useRef(false)

    useEffect(() => {
        const href = window.location.href;
        const q = (key) => getQueryString(href, key);
        setDomain(q('bL_LoginDomain') ?? q('loginDomain'))
        setSessionKey(q('bL_sk') ?? q('sessionKey'))
        setAlertUid(q('bL_alertUid') ?? q('alertUid'))
        setCartUid(q('bL_cartUid') ?? q('cartUid'))
        setCartExternalId(q('bL_cartExternalId') ?? q('cartExternalId'))
        setCartSourceExternalId(q('bL_cartsourceExternalId') ?? q('cartSourceExternalId'))
        setReturnUrl(getReturnUrl(href))
    }, [window.location.href]);

    const getAcknowledgePayload = useCallback(() => ({
        urls: serviceUrls,
        accessKey: sessionKey,
        cartUid,
        cartExternalId,
        cartSourceExternalId,
        alertUid,
    }), [serviceUrls, sessionKey, cartUid, cartExternalId, cartSourceExternalId, alertUid])

    useEffect(() => {
        if (acknowledgedRef.current) {
            return
        }
        if (!alertUid || !hasCartIdentifier(cartUid, cartExternalId, cartSourceExternalId) || !serviceUrls?.LoyaltyEngineApiUrl) {
            return
        }
        acknowledgedRef.current = true
        acknowledge.mutate(getAcknowledgePayload())
    }, [alertUid, cartUid, cartExternalId, cartSourceExternalId, serviceUrls?.LoyaltyEngineApiUrl, sessionKey, getAcknowledgePayload, acknowledge])

    const handleClose = useCallback(() => {
        finishAlertSnippetClose({
            returnUrl: returnUrl || getReturnUrl(window.location.href),
            acknowledgeAlert: alertUid && serviceUrls?.LoyaltyEngineApiUrl && acknowledge.status !== 'success'
                ? () => acknowledge.mutateAsync(getAcknowledgePayload())
                : null,
        })
    }, [alertUid, serviceUrls, acknowledge, getAcknowledgePayload, returnUrl])

    useEffect(() => {
        if (apply.status === 'success') {
            updateCouponStatus(apply.data.data.Code)
            setApplyCouponCode('')
        }
    }, [apply.status]);

    useEffect(() => {
        if (alert)
            setCoupons(mapCoupons(parseCoupons(alert.AdditionalData)))
    }, [alert])

    useEffect(() => {
        if (applyCouponCode)
            applyCoupon(applyCouponCode)
    }, [applyCouponCode])

    
    const parseCoupons = (couponList) => {
        var coupons = []
        couponList.forEach(d => {
            try {
                const coupon = JSON.parse(d)
                coupons.push(coupon)
            } catch (e) {
                console.log(e)
            }
        })
        
        return coupons
    }
    
    const mapCoupons = (couponList) => {
        return couponList.sort((a, b) => a.Name.localeCompare(b.Name)).map(d => {
            return {
                ...d,
                Applied: false
            }
        })
    }
    
    const updateCouponStatus = (code) => {
        const couponList = []
        coupons.forEach(c => {
            if (c.Code === code)
               couponList.push({
                    ...c,
                    Applied: true
                })
            else
                couponList.push(c)
        })
        setCoupons(couponList)
    }
    
    const applyCoupon = (couponCode) => {
        apply.mutate({urls: serviceUrls, cartUid, cartExternalId, cartSourceExternalId, code: couponCode, quantity: 1, accessKey: sessionKey})
    }
    
    const applyCouponWrapper = (couponCode) => {
        setApplyCouponCode(couponCode)   
    }

    const couponColumns = useMemo(() => [
        {
            Header: 'Name',
            accessor: 'Name',
        },
        {
            Header: 'Code',
            accessor: 'Code',
        },
        {
            Header: 'Apply',
            accessor: 'UsageLimit',
            Cell: ({row: {original}}) => <Button className='table-button' disabled={original.Applied} onClick={() => applyCouponWrapper(original.Code)} variant='contained' color={'primary'}>Apply</Button>
        },
        {
            Header: '',
            accessor: 'applied',
            Cell: ({row: {original}}) => original.Applied ? <>Applied</> : <></>
        }
    ], [])

    return (
        <>
            <Header title={'Coupon Alert'}/>
            {cartStatus === 'success' && !cartResponse && <div> <p>Unable to find cart</p><Button onClick={handleClose} variant='contained' color={'primary'}>Close</Button></div>}
            {cartResponse && alert &&
                <div className={'container'}>
                    <div className={'snippetContainer center'}>
                        <AlertMessage message={alert.Message} />
                        <div className={`table-wrapper-div`}>
                            <BasicTable columns={couponColumns} data={coupons} hideHeader={true}/>
                            {apply.status === 'loading' ? <LoadingSpinner /> : null}
                        </div>
                        <CloseButton onClose={handleClose} label={'Close'}/>
                    </div>
                </div>
            }
            {areAnyLoading([alertStatus, serviceUrlsStatus, cartStatus]) ? <LoadingSpinner /> : null}
            {alertError ? <p className='error-text'>{alertError}</p> : null}
            {serviceUrlsError ? <p className='error-text'>{serviceUrlsError}</p> : null}
            {cartError ? <p className='error-text'>{cartError}</p> : null}
        </>
    )
}
